import { decamelizeKeys } from 'humps';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import {
  samlConfigEdit,
  samlConfigsSet,
  samlFormReset,
  samlSetInitialState,
} from '~/actions/saml';
import {
  createSAMLConfig,
  deleteSAMLConfig,
  fetchSAMLConfigs,
  fetchSAMLData,
  updateSAMLConfig,
} from '~/api/saml';
import AdminConsolePageNavHeader from '~/components/Admin/AdminConsolePageNavHeader/AdminConsolePageNavHeader';
import SAMLConfigForm from '~/components/Admin/SSOConfigPage/SAMLConfigForm';
import { testIsValidForm } from '~/components/Admin/SSOConfigPage/util';
import { showToast } from '~/components/Shared/EcToast/showToast';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import { Button } from '~/eds/src/components/Button/Button';
import { SAMLConfigEdit } from '~/features/sso-settings/SAMLConfigEdit/SAMLConfigEdit';
import { SAMLConfigWizard } from '~/features/sso-settings/SAMLConfigWizard/SAMLConfigWizard';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { injectIntl } from 'react-intl';
import { useRouting } from '~/routing/useRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { useModal } from '~/ui/hooks/modal';

function Page({
  client,
  currentUser,
  saml,
  intl,
  // actions
  samlConfigsSet,
  samlConfigEdit,
  samlSetInitialState,
}) {
  const { navigate, params } = useRouting();
  const { configId, clientId } = params;

  const isEditing = configId !== 'new';
  const isValidForm = testIsValidForm(saml);
  const invalidFormTooltip = isValidForm
    ? ''
    : intl.formatMessage({
        id: 'SamlConfigurationForm.invalidFormTooltip',
        defaultMessage: 'Some configuration fields are missing or invalid',
        description: 'Tooltip text when form validation fails',
      });
  const hasNewUsersPage = useFlag(FlagType.NewUsersPage);

  const successToastMessage = intl.formatMessage({
    id: 'SamlConfigurationForm.configurationSavedSuccess',
    defaultMessage: 'Configuration has been saved successfully.',
    description: 'Success message when SAML configuration is saved',
  });

  const errorToastMessage = intl.formatMessage({
    id: 'SamlConfigurationForm.createConfigurationError',
    defaultMessage:
      'An error occurred while creating a new SAML Configuration.',
    description: 'Error message when creating SAML configuration fails',
  });

  const { sub_domain } = currentUser.client_config;
  const { isLoading: initializing } = useAsync(
    fetchSAMLData,
    {
      clientId: clientId ?? client.id,
      clientName: sub_domain,
      configId,
      isEditing,
    },
    {
      condition: client && currentUser && configId,
      deps: [client, currentUser, configId],
      successHandler: samlConfigEdit,
    },
  );

  const parseData = () => {
    const data = {};
    const mappings = [];

    saml.sections.forEach((section) => {
      section.configs.forEach((config) => {
        const name = config.fieldName;
        const value = config.field.value;
        const snakeCasedValue = decamelizeKeys(value ?? {});

        switch (name) {
          case 'attributes':
            //Keeping legacy mapping for backwards compatibility
            data[name] = {};
            data[name].email = value.email;
            data[name].first_name = value.firstName;
            data[name].last_name = value.lastName;

            Object.keys(snakeCasedValue).forEach((key) => {
              mappings.push({
                evisort_static_attr: key,
                evisort_custom_attr: null,
                saml_attr: snakeCasedValue[key] ?? '',
                type: 'static',
              });
            });

            break;
          case 'custom_attribute_map':
            // skip if legacy mapping
            if (value?.mappings) {
              mappings.push(
                ...value.mappings.filter(
                  (mapping) => mapping.type === 'custom',
                ),
              );
              break;
            }

            Object.keys(value ?? {}).forEach((key) => {
              mappings.push({
                evisort_static_attr: null,
                evisort_custom_attr: value[key],
                saml_attr: key,
                type: 'custom',
              });
            });
            break;
          case 'policy':
            data[name] = value.value;
            break;
          case 'auth_context_class':
            /* TODO remove list wrapper when support multiple contexts */
            data[name] = [value.value];
            break;
          case 'allowed_domains':
            data[name] = value.map((v) => v.value);
            break;
          case '':
            break;
          default:
            data[name] = value;
        }
      });
    });

    data.custom_attribute_map = {
      mappings,
    };

    return data;
  };

  const navigateBack = () => {
    navigate(-1);
  };

  const { isLoading: isCreating, executor: createConfig } = useAsync(
    createSAMLConfig,
    { clientId: clientId ?? client.id, data: parseData() },
    {
      successToastMessage,
      successHandler: navigateBack,
      errorToastMessage,
    },
  );

  const { isLoading: isUpdating, executor: updateConfig } = useAsync(
    updateSAMLConfig,
    {
      clientId: clientId ?? client.id,
      configId: saml.current?.id,
      data: parseData(),
    },
    {
      successToastMessage,
      successHandler: navigateBack,
      errorToastMessage,
    },
  );

  const [modal, showModal] = useModal({
    disableCancelButton: false,
    title: intl.formatMessage({
      id: 'SamlConfigurationForm.deleteConfigurationModalTitle',
      defaultMessage: 'Delete Configuration?',
      description: 'Title for delete configuration modal',
    }),
    actionButton: {
      disabled: false,
      text: intl.formatMessage({
        id: 'SamlConfigurationForm.deleteButton',
        defaultMessage: 'Delete',
        description: 'Delete button text in modal',
      }),
      variant: 'red',
      errorHandler: () =>
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'SamlConfigurationForm.deleteConfigurationError',
            defaultMessage:
              'An error occurred while deleting SAML Configuration.',
            description: 'Error message when deleting SAML configuration fails',
          }),
        ),
      promise: async () => {
        await deleteSAMLConfig({
          clientId: clientId ?? client.id,
          configId: saml.current?.id,
        });

        const configs = await fetchSAMLConfigs({ clientId: client.id });
        samlConfigsSet(configs.results);

        navigateBack();
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'SamlConfigurationForm.configurationDeletedSuccess',
            defaultMessage: 'Configuration has been deleted successfully.',
            description: 'Success message when SAML configuration is deleted',
          }),
        );
      },
    },
    content: (
      <Text variant="xs-dense">
        {intl.formatMessage(
          {
            id: 'SamlConfigurationForm.deleteConfirmationMessage',
            defaultMessage:
              'Are you sure you want to remove the {configName} configuration? {warningText}',
            description:
              'Confirmation message for deleting SAML configuration. Example: configName = "Google SSO"',
          },
          {
            configName: saml.current?.idp_name,
            warningText: (
              <Text variant="xs-dense-bold">
                {intl.formatMessage({
                  id: 'SamlConfigurationForm.undoWarning',
                  defaultMessage: "You won't be able to undo this action.",
                  description: 'Warning text about irreversible action',
                })}
              </Text>
            ),
          },
        )}
      </Text>
    ),
  });

  const { isLoading } = useAsync(
    fetchSAMLConfigs,
    { clientId: client.id },
    {
      condition: client.id,
      deps: [client.id],
      successHandler: (response) => {
        samlFormReset();
        samlConfigsSet(response.results);
      },
    },
  );

  if (!isEditing && !!saml.configs.length && !!client.id) {
    navigate(`/admin/console/${client.id}/saml-configuration`, {
      replace: true,
    });
  }

  const handleSubmit = () => {
    if (isValidForm) {
      if (isEditing) {
        updateConfig();
      } else {
        createConfig();
      }
    } else {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'SamlConfigurationForm.validationError',
          defaultMessage: 'Some configuration fields are missing or invalid',
          description: 'Error message when form validation fails',
        }),
      );
    }
  };

  const getSAMLWizard = () => {
    if (hasNewUsersPage) {
      if (isEditing) {
        return saml?.sections?.length ? (
          <SAMLConfigEdit
            clientId={clientId ?? client.id}
            onDelete={showModal}
            onSubmit={handleSubmit}
            onHide={navigateBack}
            sections={saml.sections}
          />
        ) : null;
      } else {
        return saml?.sections?.length ? (
          <SAMLConfigWizard
            clientId={clientId ?? client.id}
            onSubmit={handleSubmit}
            onHide={navigateBack}
            sections={saml.sections}
          />
        ) : null;
      }
    } else {
      return <SAMLConfigForm />;
    }
  };

  useEffect(() => {
    samlSetInitialState();
  }, []);

  return (
    <div data-testid="party-list-tab">
      {initializing || isCreating || isUpdating || isLoading ? (
        <FlexLayout justifyContent="center">
          <LoadingSpinner size="medium" />
        </FlexLayout>
      ) : (
        <>
          <FlexLayout
            alignItems="center"
            justifyContent="space-between"
            sx={{ minWidth: '1200px' }}
          >
            {!isEditing && (
              <>
                <AdminConsolePageNavHeader
                  title={intl.formatMessage({
                    id: 'SamlConfigurationForm.newConfigurationTitle',
                    defaultMessage: 'New Configuration',
                    description: 'Title for new SAML configuration page',
                  })}
                  navigateBack={navigateBack}
                />
                <FlexLayout alignItems="center" space={4} px={16}>
                  <Button
                    text={intl.formatMessage({
                      id: 'SamlConfigurationForm.saveConfigurationButton',
                      defaultMessage: 'Save Configuration',
                      description: 'Button text to save SAML configuration',
                    })}
                    disabled={!isValidForm}
                    onClick={() => createConfig()}
                    tooltip={invalidFormTooltip}
                    variant="primary"
                  />
                </FlexLayout>
              </>
            )}

            {isEditing && (
              <>
                <AdminConsolePageNavHeader
                  title={intl.formatMessage({
                    id: 'SamlConfigurationForm.editConfigurationTitle',
                    defaultMessage: 'Edit Configuration',
                    description: 'Title for edit SAML configuration page',
                  })}
                  navigateBack={navigateBack}
                />
                <FlexLayout alignItems="center" space={4} px={16}>
                  <Button
                    text={intl.formatMessage({
                      id: 'SamlConfigurationForm.updateConfigurationButton',
                      defaultMessage: 'Update Configuration',
                      description: 'Button text to update SAML configuration',
                    })}
                    disabled={!isValidForm}
                    onClick={() => updateConfig()}
                    tooltip={invalidFormTooltip}
                    variant="primary"
                  />
                  <Button
                    text={intl.formatMessage({
                      id: 'SamlConfigurationForm.deleteConfigurationButton',
                      defaultMessage: 'Delete Configuration',
                      description: 'Button text to delete SAML configuration',
                    })}
                    onClick={showModal}
                  />
                </FlexLayout>
              </>
            )}
          </FlexLayout>
          {getSAMLWizard()}
        </>
      )}
      {modal}
    </div>
  );
}

const mapStateToProps = ({ saml, client, currentUser }) => ({
  saml,
  client,
  currentUser,
});

const mapDispatchToProps = (dispatch, { intl }) => ({
  samlConfigsSet: (configs) => dispatch(samlConfigsSet(configs)),
  samlConfigEdit: (data) => dispatch(samlConfigEdit(data)),
  samlSetInitialState: () => dispatch(samlSetInitialState({ intl })),
});

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(Page));
